<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0">
      <div
        v-if="visible"
        class="fixed inset-0 z-[99999] select-none"
        style="touch-action: none">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black"
          :style="{
            opacity: backdropOpacity,
            transition: dismissAnimating
              ? 'opacity 0.3s cubic-bezier(0.2, 0, 0, 1)'
              : 'none'
          }" />

        <!-- Image viewport -->
        <div
          ref="viewport"
          class="absolute inset-0 overflow-hidden"
          @touchstart.prevent="onTouchStart"
          @touchmove.prevent="onTouchMove"
          @touchend="onTouchEnd"
          @wheel.prevent="onWheel">
          <!-- 3-slot horizontal track -->
          <div :style="trackStyle" class="absolute inset-y-0 flex">
            <!-- Prev slot -->
            <div
              class="relative shrink-0 overflow-hidden"
              :style="{ width: viewportWidth + 'px' }">
              <img
                v-if="prevImage"
                :src="prevImage"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain pointer-events-none"
                :class="imgSizeClass"
                draggable="false" />
            </div>
            <!-- Current slot -->
            <div
              class="relative shrink-0 overflow-hidden"
              :style="{ width: viewportWidth + 'px' }">
              <img
                v-if="currentImage"
                ref="imageEl"
                :src="currentImage"
                :style="imageTransformStyle"
                class="absolute top-1/2 left-1/2 object-contain pointer-events-none"
                :class="imgSizeClass"
                draggable="false"
                @load="onImageLoad" />

              <!-- Loading spinner -->
              <div
                v-if="!imageLoaded"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <fa-icon
                  :icon="['fas', 'spinner-third']"
                  class="text-2xl text-white/40 fa-spin" />
              </div>
            </div>
            <!-- Next slot -->
            <div
              class="relative shrink-0 overflow-hidden"
              :style="{ width: viewportWidth + 'px' }">
              <img
                v-if="nextImage"
                :src="nextImage"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain pointer-events-none"
                :class="imgSizeClass"
                draggable="false" />
            </div>
          </div>
        </div>

        <!-- Close button (top-right, safe area aware) -->
        <button
          class="absolute right-4 z-10 flex justify-center items-center w-10 h-10 rounded-full touch-manipulation text-white/70 hover:text-white bg-black/40"
          :style="{ top: `calc(env(safe-area-inset-top, 0px) + 12px)` }"
          @click="close">
          <fa-icon icon="xmark" class="text-lg" />
        </button>

        <!-- Prev/Next arrows (only when not in pan mode and multiple images) -->
        <template v-if="!isInPanMode && imgs.length > 1">
          <button
            v-if="currentIndex > 0"
            class="absolute left-2 top-1/2 z-10 p-3 -translate-y-1/2 touch-manipulation text-white/50 hover:text-white"
            @click="prev">
            <fa-icon icon="chevron-left" class="text-xl" />
          </button>
          <button
            v-if="currentIndex < imgs.length - 1"
            class="absolute right-2 top-1/2 z-10 p-3 -translate-y-1/2 touch-manipulation text-white/50 hover:text-white"
            @click="next">
            <fa-icon icon="chevron-right" class="text-xl" />
          </button>
        </template>

        <!-- Thumbnail strip -->
        <div
          v-if="imgs.length > 1 && !isInPanMode"
          class="absolute left-0 right-0 z-10 flex justify-center"
          :style="{
            bottom: `calc(env(safe-area-inset-bottom, 0px) + 60px)`
          }">
          <div
            ref="thumbStrip"
            class="lightbox-thumb-strip flex gap-1.5 px-3 py-1.5 overflow-x-auto max-w-[80vw]">
            <button
              v-for="(img, i) in imgs"
              :key="i"
              class="shrink-0 w-12 h-12 rounded-lg overflow-hidden touch-manipulation transition-all duration-200"
              :class="i === currentIndex
                ? 'ring-2 ring-white opacity-100 scale-110'
                : 'opacity-40 hover:opacity-60'"
              @click="jumpToImage(i)">
              <img
                :src="getThumbnailUrl(img)"
                class="w-full h-full object-cover"
                draggable="false" />
            </button>
          </div>
        </div>

        <!-- Bottom toolbar (safe area aware) -->
        <div
          class="absolute bottom-0 right-0 left-0 z-10 flex justify-center"
          :style="{
            paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 12px)`
          }">
          <div
            class="flex items-center gap-1 px-3 py-2 rounded-xl bg-black/60 backdrop-blur-sm">
            <span
              v-if="imgs.length > 1"
              class="px-2 text-xs tabular-nums text-gray-400">
              {{ currentIndex + 1 }}/{{ imgs.length }}
            </span>

            <div
              v-if="imgs.length > 1"
              class="w-px h-4 bg-white/20 mx-0.5" />

            <button
              class="p-1.5 text-gray-300 transition-colors hover:text-white touch-manipulation"
              title="Zoom out"
              @click="handleZoomOut">
              <fa-icon icon="minus" class="text-xs" />
            </button>
            <button
              class="p-1.5 text-gray-300 transition-colors hover:text-white touch-manipulation"
              title="Zoom in"
              @click="handleZoomIn">
              <fa-icon icon="plus" class="text-xs" />
            </button>

            <div class="w-px h-4 bg-white/20 mx-0.5" />

            <div
              class="p-1.5 transition-colors"
              :class="isInPanMode ? 'text-gray-300' : 'text-gray-500'"
              title="Pinch to zoom, drag to pan">
              <fa-icon
                :icon="['far', 'arrows-up-down-left-right']"
                class="text-xs" />
            </div>

            <div v-if="isZoomed" class="w-px h-4 bg-white/20 mx-0.5" />

            <button
              v-if="isZoomed"
              class="px-2 py-1 text-xs text-indigo-400 transition-colors hover:text-indigo-300 touch-manipulation"
              @click="resetZoomAnimated">
              reset
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  visible: Boolean,
  imgs: Array,
  index: Number
})

const emit = defineEmits(['hide'])

// Refs
const viewport = ref(null)
const imageEl = ref(null)
const thumbStrip = ref(null)
const currentIndex = ref(0)
const imageLoaded = ref(false)

// Transform state
const scale = ref(1)
const tx = ref(0)
const ty = ref(0)
const animating = ref(false)

// Swipe state
const swipeOffset = ref(0)
const swipeAnimating = ref(false)
const viewportWidth = ref(window.innerWidth)

// Pan/dismiss state
const panMode = ref(false)
const dismissOffset = ref(0)
const dismissAnimating = ref(false)

// Non-reactive state
let touchState = null
let preloadCache = []
let swipeTransitionTimer = null

// Computed
const isZoomed = computed(() => scale.value > 1.05)
const isInPanMode = computed(() => panMode.value || scale.value > 1.05)
const currentImage = computed(() => props.imgs?.[currentIndex.value] || '')

const prevImage = computed(() => {
  const i = currentIndex.value - 1
  return i >= 0 ? props.imgs?.[i] : null
})

const nextImage = computed(() => {
  const i = currentIndex.value + 1
  return i < (props.imgs?.length || 0) ? props.imgs[i] : null
})

const imgSizeClass = computed(() => {
  const hasStrip = (props.imgs?.length || 0) > 1 && !isInPanMode.value
  return hasStrip
    ? 'max-w-[calc(100vw-16px)] max-h-[calc(100vh-180px)]'
    : 'max-w-[calc(100vw-16px)] max-h-[calc(100vh-120px)]'
})

const backdropOpacity = computed(() => {
  const d = dismissOffset.value
  if (d <= 0) return 0.95
  const progress = Math.min(1, d / 300)
  return 0.95 * (1 - progress * 0.7)
})

const trackStyle = computed(() => {
  const offsetX = -viewportWidth.value + swipeOffset.value
  const offsetY = dismissOffset.value
  return {
    width: `${viewportWidth.value * 3}px`,
    transform: `translate(${offsetX}px, ${offsetY}px)`,
    transition: swipeAnimating.value || dismissAnimating.value
      ? 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)'
      : 'none',
    willChange: 'transform'
  }
})

const imageTransformStyle = computed(() => ({
  transform: `translate(-50%, -50%) translate(${tx.value}px, ${ty.value}px) scale(${scale.value})`,
  transition: animating.value
    ? 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)'
    : 'none',
  willChange: 'transform'
}))

// Helpers
const getDistance = (t1, t2) =>
  Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)

const getMidpoint = (t1, t2) => ({
  x: (t1.clientX + t2.clientX) / 2,
  y: (t1.clientY + t2.clientY) / 2
})

const getThumbnailUrl = (url) => {
  return url.replace(/\/t\/p\/\w+\//, '/t/p/w92/')
}

// Preload adjacent images into browser cache
const preloadAdjacentImages = () => {
  preloadCache = []
  const indices = [
    currentIndex.value - 1,
    currentIndex.value + 1,
    currentIndex.value + 2
  ]
  for (const i of indices) {
    if (i >= 0 && i < (props.imgs?.length || 0)) {
      const img = new Image()
      img.src = props.imgs[i]
      preloadCache.push(img)
    }
  }
}

// Zoom controls
const resetZoom = () => {
  scale.value = 1
  tx.value = 0
  ty.value = 0
  animating.value = false
}

const resetZoomAnimated = () => {
  animating.value = true
  scale.value = 1
  tx.value = 0
  ty.value = 0
  setTimeout(() => {
    animating.value = false
  }, 300)
}

const constrainPan = () => {
  if (scale.value <= 1) {
    tx.value = 0
    ty.value = 0
    return
  }
  const img = imageEl.value
  if (!img || !img.offsetWidth) return
  const maxTx = Math.max(
    0,
    (img.offsetWidth * scale.value - window.innerWidth) / 2
  )
  const maxTy = Math.max(
    0,
    (img.offsetHeight * scale.value - window.innerHeight) / 2
  )
  tx.value = Math.max(-maxTx, Math.min(maxTx, tx.value))
  ty.value = Math.max(-maxTy, Math.min(maxTy, ty.value))
}

const handleZoomIn = () => {
  animating.value = true
  scale.value = Math.min(5, scale.value * 1.5)
  setTimeout(() => {
    animating.value = false
  }, 300)
}

const handleZoomOut = () => {
  animating.value = true
  const newScale = scale.value / 1.5
  if (newScale <= 1.05) {
    resetZoomAnimated()
  } else {
    scale.value = newScale
    constrainPan()
    setTimeout(() => {
      animating.value = false
    }, 300)
  }
}

// Swipe navigation
const completeSwipe = (direction) => {
  const target = direction === 'prev'
    ? viewportWidth.value
    : -viewportWidth.value
  swipeAnimating.value = true
  swipeOffset.value = target

  swipeTransitionTimer = setTimeout(() => {
    swipeAnimating.value = false
    currentIndex.value += direction === 'prev' ? -1 : 1
    swipeOffset.value = 0
    resetZoom()
    imageLoaded.value = false
    swipeTransitionTimer = null
  }, 300)
}

const cancelSwipe = () => {
  swipeAnimating.value = true
  swipeOffset.value = 0
  setTimeout(() => {
    swipeAnimating.value = false
  }, 300)
}

// Dismiss close: animate image off-screen, then hide
const dismissClose = () => {
  dismissAnimating.value = true
  dismissOffset.value = window.innerHeight
  setTimeout(() => {
    dismissOffset.value = 0
    dismissAnimating.value = false
    close()
  }, 300)
}

// Navigation
const close = () => {
  if (swipeTransitionTimer) {
    clearTimeout(swipeTransitionTimer)
    swipeTransitionTimer = null
  }
  swipeOffset.value = 0
  swipeAnimating.value = false
  dismissOffset.value = 0
  dismissAnimating.value = false
  panMode.value = false
  resetZoom()
  emit('hide')
}

const prev = () => {
  if (currentIndex.value > 0 && !swipeAnimating.value) {
    completeSwipe('prev')
  }
}

const next = () => {
  if (currentIndex.value < props.imgs.length - 1 && !swipeAnimating.value) {
    completeSwipe('next')
  }
}

const jumpToImage = (i) => {
  if (i === currentIndex.value || swipeAnimating.value) return
  const diff = i - currentIndex.value
  if (Math.abs(diff) === 1) {
    completeSwipe(diff > 0 ? 'next' : 'prev')
  } else {
    currentIndex.value = i
    resetZoom()
    imageLoaded.value = false
  }
}

const onImageLoad = () => {
  imageLoaded.value = true
}

// Thumbnail auto-scroll
const scrollThumbToCenter = () => {
  nextTick(() => {
    const strip = thumbStrip.value
    if (!strip) return
    const thumb = strip.children[currentIndex.value]
    if (!thumb) return
    const scrollTarget =
      thumb.offsetLeft - strip.offsetWidth / 2 + thumb.offsetWidth / 2
    strip.scrollTo({ left: scrollTarget, behavior: 'smooth' })
  })
}

// Touch handlers
const onTouchStart = (e) => {
  animating.value = false
  dismissAnimating.value = false

  // Rapid swipe guard: resolve pending swipe animation
  if (swipeTransitionTimer) {
    clearTimeout(swipeTransitionTimer)
    swipeTransitionTimer = null
    if (Math.abs(swipeOffset.value) > viewportWidth.value / 2) {
      if (swipeOffset.value > 0 && currentIndex.value > 0) {
        currentIndex.value--
      } else if (
        swipeOffset.value < 0 &&
        currentIndex.value < props.imgs.length - 1
      ) {
        currentIndex.value++
      }
    }
    swipeOffset.value = 0
    swipeAnimating.value = false
    dismissOffset.value = 0
    resetZoom()
  }

  if (e.touches.length === 2) {
    const [t1, t2] = e.touches
    touchState = {
      type: 'pinch',
      initialDistance: getDistance(t1, t2),
      initialScale: scale.value,
      initialTx: tx.value,
      initialTy: ty.value
    }
  } else if (e.touches.length === 1) {
    const touch = e.touches[0]
    touchState = {
      type: isInPanMode.value ? 'pan' : 'swipe',
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      lastTime: Date.now(),
      startTx: tx.value,
      startTy: ty.value,
      directionLocked: null,
      moved: false
    }
  }
}

const onTouchMove = (e) => {
  if (!touchState) return

  if (e.touches.length === 2) {
    // Pinch zoom
    if (touchState.type !== 'pinch') {
      const [t1, t2] = e.touches
      touchState = {
        type: 'pinch',
        initialDistance: getDistance(t1, t2),
        initialScale: scale.value,
        initialTx: tx.value,
        initialTy: ty.value
      }
      // Cancel any in-progress swipe or dismiss
      if (swipeOffset.value !== 0) swipeOffset.value = 0
      if (dismissOffset.value !== 0) dismissOffset.value = 0
      return
    }

    const [t1, t2] = e.touches
    const dist = getDistance(t1, t2)
    const center = getMidpoint(t1, t2)
    const scaleDelta = dist / touchState.initialDistance
    const newScale = Math.max(
      0.5,
      Math.min(5, touchState.initialScale * scaleDelta)
    )

    const vp = viewport.value
    if (vp) {
      const rect = vp.getBoundingClientRect()
      const cx = center.x - rect.left - rect.width / 2
      const cy = center.y - rect.top - rect.height / 2
      const scaleRatio = newScale / scale.value
      tx.value = cx - scaleRatio * (cx - tx.value)
      ty.value = cy - scaleRatio * (cy - ty.value)
    }

    scale.value = newScale
  } else if (e.touches.length === 1) {
    const touch = e.touches[0]
    const dx = touch.clientX - touchState.startX
    const dy = touch.clientY - touchState.startY

    if (touchState.type === 'pan' && scale.value > 1.05) {
      // Actually zoomed: unrestricted pan
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        touchState.moved = true
      }
      tx.value = touchState.startTx + dx
      ty.value = touchState.startTy + dy
    } else {
      // Swipe mode OR pan mode at default zoom: direction-locked gestures
      if (!touchState.directionLocked) {
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          touchState.directionLocked =
            Math.abs(dx) >= Math.abs(dy) ? 'horizontal' : 'vertical'
        }
      }

      if (
        touchState.directionLocked === 'horizontal' &&
        touchState.type === 'swipe'
      ) {
        // Horizontal swipe navigation (swipe mode only)
        touchState.moved = true
        let offset = dx
        const atStart = currentIndex.value === 0 && dx > 0
        const atEnd =
          currentIndex.value === props.imgs.length - 1 && dx < 0
        if (atStart || atEnd) offset = dx * 0.3
        swipeOffset.value = offset
        touchState.lastX = touch.clientX
        touchState.lastTime = Date.now()
      } else if (touchState.directionLocked === 'vertical') {
        // Vertical dismiss / mode-switch
        touchState.moved = true
        dismissOffset.value = dy
        touchState.lastY = touch.clientY
        touchState.lastTime = Date.now()
      }
    }
  }
}

const onTouchEnd = (e) => {
  if (!touchState) return

  if (e.touches.length === 0) {
    // Snap back if pinched below 1
    if (scale.value < 1) {
      resetZoomAnimated()
    } else if (scale.value > 1.05) {
      animating.value = true
      constrainPan()
      setTimeout(() => {
        animating.value = false
      }, 300)
    }

    // Handle horizontal swipe completion (swipe mode only)
    if (
      touchState.type === 'swipe' &&
      touchState.directionLocked === 'horizontal' &&
      e.changedTouches.length === 1
    ) {
      const endX = e.changedTouches[0].clientX
      const dt = Math.max(1, Date.now() - touchState.lastTime)
      const velocity = (endX - touchState.lastX) / dt

      const offset = swipeOffset.value
      const threshold = viewportWidth.value * 0.25
      const shouldComplete =
        Math.abs(offset) > threshold || Math.abs(velocity) > 0.3

      if (shouldComplete && offset > 0 && currentIndex.value > 0) {
        completeSwipe('prev')
      } else if (
        shouldComplete &&
        offset < 0 &&
        currentIndex.value < props.imgs.length - 1
      ) {
        completeSwipe('next')
      } else if (swipeOffset.value !== 0) {
        cancelSwipe()
      }
    } else if (touchState.type === 'swipe' && swipeOffset.value !== 0) {
      cancelSwipe()
    }

    // Handle vertical dismiss / mode-switch
    if (dismissOffset.value !== 0 && e.changedTouches.length === 1) {
      const endY = e.changedTouches[0].clientY
      const dt = Math.max(1, Date.now() - touchState.lastTime)
      const velocityY = (endY - touchState.lastY) / dt
      const offset = dismissOffset.value
      const shouldComplete =
        Math.abs(offset) > 80 || Math.abs(velocityY) > 0.3

      if (shouldComplete && offset > 0) {
        // Swipe down
        if (panMode.value) {
          // Exit pan mode
          panMode.value = false
          dismissAnimating.value = true
          dismissOffset.value = 0
          setTimeout(() => {
            dismissAnimating.value = false
          }, 300)
        } else {
          // Close lightbox
          dismissClose()
        }
      } else if (shouldComplete && offset < 0 && !panMode.value) {
        // Swipe up → enter pan mode
        panMode.value = true
        dismissAnimating.value = true
        dismissOffset.value = 0
        setTimeout(() => {
          dismissAnimating.value = false
        }, 300)
      } else {
        // Snap back
        dismissAnimating.value = true
        dismissOffset.value = 0
        setTimeout(() => {
          dismissAnimating.value = false
        }, 300)
      }
    }

    touchState = null
  } else if (e.touches.length === 1 && touchState.type === 'pinch') {
    // Went from 2 fingers to 1 — switch to pan mode
    const touch = e.touches[0]
    touchState = {
      type: 'pan',
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      lastTime: Date.now(),
      startTx: tx.value,
      startTy: ty.value,
      directionLocked: null,
      moved: false
    }
  }
}

// Mouse wheel zoom (desktop)
const onWheel = (e) => {
  const delta = e.deltaY > 0 ? 0.9 : 1.1
  const newScale = Math.max(0.5, Math.min(5, scale.value * delta))

  if (newScale <= 1.05 && scale.value > 1.05) {
    resetZoomAnimated()
    return
  }

  const vp = viewport.value
  if (vp) {
    const rect = vp.getBoundingClientRect()
    const cx = e.clientX - rect.left - rect.width / 2
    const cy = e.clientY - rect.top - rect.height / 2
    const scaleRatio = newScale / scale.value
    tx.value = cx - scaleRatio * (cx - tx.value)
    ty.value = cy - scaleRatio * (cy - ty.value)
  }

  scale.value = newScale
}

// Keyboard
const onKeyDown = (e) => {
  if (!props.visible) return
  switch (e.key) {
    case 'Escape':
      close()
      break
    case 'ArrowLeft':
      if (!isInPanMode.value) prev()
      break
    case 'ArrowRight':
      if (!isInPanMode.value) next()
      break
  }
}

// Resize handler
const onResize = () => {
  viewportWidth.value = window.innerWidth
}

// Lifecycle
watch(
  () => props.visible,
  (val) => {
    if (val) {
      currentIndex.value = props.index || 0
      resetZoom()
      swipeOffset.value = 0
      swipeAnimating.value = false
      dismissOffset.value = 0
      dismissAnimating.value = false
      panMode.value = false
      imageLoaded.value = false
      document.body.style.overflow = 'hidden'
      preloadAdjacentImages()
    } else {
      document.body.style.overflow = ''
      if (swipeTransitionTimer) {
        clearTimeout(swipeTransitionTimer)
        swipeTransitionTimer = null
      }
    }
  }
)

watch(
  () => props.index,
  (val) => {
    if (props.visible) {
      currentIndex.value = val || 0
      resetZoom()
      swipeOffset.value = 0
      swipeAnimating.value = false
      dismissOffset.value = 0
      dismissAnimating.value = false
      imageLoaded.value = false
    }
  }
)

watch(currentIndex, () => {
  preloadAdjacentImages()
  scrollThumbToCenter()
})

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('resize', onResize)
  document.body.style.overflow = ''
  if (swipeTransitionTimer) {
    clearTimeout(swipeTransitionTimer)
  }
})
</script>

<style scoped>
.lightbox-thumb-strip::-webkit-scrollbar {
  display: none;
}
.lightbox-thumb-strip {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
